import React, { useEffect, useRef, useState } from 'react';

import { API_FETCHING } from '@nw/api-constants';

import { DangerIcon } from '../../icons/DangerIcon';

import { showErrorToast } from '../ToastUtils/ToastUtils';

// keyCode constants
const BACKSPACE = 8;
const LEFT_ARROW = 37;
const RIGHT_ARROW = 39;
const DELETE = 46;
const SPACEBAR = 32;

// Doesn't really check if it's a style Object
// Basic implementation to check if it's not a string
// of classNames and is an Object
// TODO: Better implementation
const isStyleObject = (obj) => typeof obj === 'object';
const isNumber = (n) => {
  return /\d+/g.test(n);
};

const SingleOtpInput = (props) => {
  const input = useRef<HTMLInputElement>(null);

  const getClasses = (...classes) =>
    classes.filter((c) => !isStyleObject(c) && c !== false).join(' ');

  const getType = () => {
    const { isInputSecure, isInputNum } = props;

    if (isInputSecure) {
      return 'password';
    }

    if (isInputNum) {
      return 'tel';
    }

    return 'text';
  };

  const {
    placeholder,
    separator,
    isLastChild,
    inputStyle,
    focus,
    isDisabled,
    hasErrored,
    errorStyle,
    focusStyle,
    disabledStyle,
    shouldAutoFocus,
    isInputNum,
    index,
    value,
    className,
    isInputSecure,
    onBlur,
    isFocus,
    disabled,
    maxLengthForFirst,
    ...rest
  } = props;

  useEffect(() => {
    const { current: inputEl } = input;

    if (focus && inputEl) {
      inputEl.focus();
      inputEl.select();
    }
  }, [focus]);

  return (
    <input
      aria-label={`${index === 0 ? 'Please enter verification code. ' : ''}${isInputNum ? 'Digit' : 'Character'
        } ${index + 1}`}
      autoComplete={index === 0 ? 'one-time-code' : 'off'}
      style={Object.assign(
        { textAlign: 'center' },
        isStyleObject(inputStyle) && inputStyle,
        focus && isStyleObject(focusStyle) && focusStyle,
        isDisabled && isStyleObject(disabledStyle) && disabledStyle,
        hasErrored && !isFocus && isStyleObject(errorStyle) && errorStyle
      )}
      placeholder={placeholder}
      className={getClasses(
        inputStyle,
        focus && focusStyle,
        isDisabled && disabledStyle,
        hasErrored && !isFocus && errorStyle
      )}
      type={getType()}
      inputMode="numeric"
      pattern={'[0-9]*'}
      maxLength={index === 0 && maxLengthForFirst ? maxLengthForFirst : 1}
      ref={input}
      disabled={isDisabled || disabled}
      value={value ? value : ''}
      onBlur={onBlur}
      isFocus={isFocus}
      {...rest}
    />
  );
};

const OtpInput = (props) => {
  const { numInputs = 4, value = '' } = props;

  const [activeInput, setActiveInput] = useState(0);

  // Get OTP value as array, handling space placeholders for empty positions
  const getOtpValue = () => {
    if (!value) return [];
    // Convert space placeholders back to empty strings for display
    return value.toString().split('').map(char => char === ' ' ? '' : char);
  };

  const getFirstEmptyIndex = React.useCallback(() => {
    const otp = value
      ? value
        .toString()
        .split('')
        .map((char) => (char === ' ' ? '' : char))
      : [];
    for (let i = 0; i < numInputs; i++) {
      // Check for empty string or undefined (space placeholders are converted to '' by getOtpValue)
      if (!otp[i] || otp[i] === ' ') return i;
    }
    return numInputs - 1; // All filled, return last index
  }, [numInputs, value]);

  const getPlaceholderValue = () => {
    const { placeholder, numInputs } = props;

    if (typeof placeholder === 'string') {
      if (placeholder.length === numInputs) {
        return placeholder;
      }

      if (placeholder.length > 0) {
        console.error(
          'Length of the placeholder should be equal to the number of inputs.'
        );
      }
    }
    return '';
  };

  const needsFocusRestoration = useRef(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (props.apiStatus === API_FETCHING) {
      setActiveInput(-1);
      needsFocusRestoration.current = true;
      return;
    }

    const currentValue = value ? value.toString() : '';

    // If OTP was reset/cleared, move focus back to the first box.
    // NOTE: we use trim() so a fully-empty OTP stored as "      " resets too.
    if (!currentValue || currentValue.trim() === '') {
      setActiveInput(0);
      needsFocusRestoration.current = false;
      return;
    }

    // If we were disabled due to API state, restore focus to the first empty box.
    // Avoid constantly resetting activeInput on every keystroke to prevent UI flicker.
    if (needsFocusRestoration.current) {
      setActiveInput(getFirstEmptyIndex());
      needsFocusRestoration.current = false;
    }
  }, [props.apiStatus, value, numInputs, getFirstEmptyIndex]);

  // Helper to return OTP from input
  // Uses space as placeholder for empty positions to preserve digit positions
  const handleOtpChange = (otp) => {
    const { onChange } = props;
    // Use space as placeholder for empty positions to maintain array positions
    // This prevents digits from shifting when a middle digit is deleted
    const otpValue = otp.map(char => char || ' ').join('');

    onChange(otpValue);
  };

  const isInputValueValid = (value) => {
    if (props.isInputNum) {
      // Strictly check if value is a single digit (0-9)
      return /^\d$/.test(value);
    }
    return typeof value === 'string' && value.trim().length === 1;
  };

  // Focus on input by index
  const focusInput = (input) => {
    const { numInputs } = props;
    const activeInput = Math.max(Math.min(numInputs - 1, input), 0);
    setActiveInput(activeInput);
  };

  // Focus on next input
  const focusNextInput = () => {
    focusInput(activeInput + 1);
  };

  // Focus on previous input
  const focusPrevInput = () => {
    focusInput(activeInput - 1);
  };

  // Get the index of the first empty input box
  // This helps enforce sequential filling
  // Change OTP value at focused input
  const changeCodeAtFocus = (value) => {
    const otp = getOtpValue();
    otp[activeInput] = value[0];
    handleOtpChange(otp);
  };

  // Handle pasted OTP
  const handleOnPaste = (e) => {
    e.preventDefault();
    const { numInputs, isDisabled, isInputNum } = props;

    if (isDisabled) {
      return;
    }

    // Initialize OTP array with proper length, filling with empty strings
    const currentOtp = getOtpValue();
    const otp = Array(numInputs).fill('').map((_, index) => currentOtp[index] || '');

    // Get pastedData and filter to only numeric characters if isInputNum is true
    let pastedData = e.clipboardData
      .getData('text/plain')
      .slice(0, numInputs - activeInput)
      .split('');

    // Filter out non-numeric characters if this is a numeric input
    if (isInputNum) {
      pastedData = pastedData.filter(char => /^\d$/.test(char));
      if (pastedData.length === 0) {
        if (props.showToast) {
          props.showToast();
        }
        return;
      }
    } else if (!isNumber(pastedData.join(''))) {
      if (props.showToast) {
        props.showToast();
      }
    }

    // Paste data from focused input onwards
    let nextActiveInput = activeInput;
    for (let pos = activeInput; pos < numInputs && pastedData.length > 0; ++pos) {
      otp[pos] = pastedData.shift();
      nextActiveInput = pos + 1;
    }

    // Clamp nextActiveInput to valid range
    nextActiveInput = Math.min(nextActiveInput, numInputs - 1);
    handleOtpChange(otp);
    focusInput(nextActiveInput);
  };

  const handleOnChange = (e) => {
    let { value } = e.target;
    const { numInputs, isInputNum } = props;

    // Filter out non-numeric characters if this is a numeric input
    if (isInputNum) {
      value = value.replace(/\D/g, '');
      if (value !== e.target.value) {
        // Non-numeric characters were removed, show toast
        if (props.showToast) {
          props.showToast();
        } else if (props.t) {
          showErrorToast(props.t('common.pleaseEnterNumbers'), {
            icon: () => <DangerIcon />
          });
        }
      }
    } else if (!isNumber(value)) {
      if (props.showToast) {
        props.showToast();
      } else if (props.t) {
        showErrorToast(props.t('common.pleaseEnterNumbers'), {
          icon: () => <DangerIcon />
        });
      }
    }

    // Mobile keyboard autofill (one-time-code): multiple digits land in first input
    if (value.length > 1) {
      const digits = (isInputNum ? value.replace(/\D/g, '') : value)
        .slice(0, numInputs)
        .split('');
      const currentOtp = getOtpValue();
      const otp = Array(numInputs)
        .fill('')
        .map((_, idx) => digits[idx] ?? currentOtp[idx] ?? '');
      handleOtpChange(otp);
      focusInput(Math.min(digits.length, numInputs) - 1);
      return;
    }

    if (isInputValueValid(value)) {
      changeCodeAtFocus(value);
    }
  };

  // Handle cases of backspace, delete, left arrow, right arrow, space
  const handleOnKeyDown = (e) => {
    const { isInputNum } = props;

    // Allow control keys and navigation keys
    const isControlKey =
      e.keyCode === BACKSPACE || e.key === 'Backspace' ||
      e.keyCode === DELETE || e.key === 'Delete' ||
      e.keyCode === LEFT_ARROW || e.key === 'ArrowLeft' ||
      e.keyCode === RIGHT_ARROW || e.key === 'ArrowRight' ||
      e.keyCode === SPACEBAR || e.key === ' ' || e.key === 'Spacebar' || e.key === 'Space' ||
      e.ctrlKey || e.metaKey || e.altKey ||
      e.key === 'Tab' || e.key === 'Enter';

    // For numeric inputs, prevent non-numeric keys
    if (isInputNum && !isControlKey) {
      // Allow only digits (0-9)
      if (!/^\d$/.test(e.key)) {
        e.preventDefault();
        if (props.showToast) {
          props.showToast();
        }
        return;
      }
    }

    if (e.keyCode === BACKSPACE || e.key === 'Backspace') {
      e.preventDefault();
      const otp = getOtpValue();

      // If current box has a value, clear it but keep focus on the same box
      if (otp[activeInput]) {
        otp[activeInput] = '';
        handleOtpChange(otp);
        focusInput(activeInput);
        return;
      }

      // If current box is empty, move to previous box and clear it
      const prevIndex = Math.max(activeInput - 1, 0);
      if (prevIndex !== activeInput) {
        otp[prevIndex] = '';
        handleOtpChange(otp);
        focusInput(prevIndex);
      }
    } else if (e.keyCode === DELETE || e.key === 'Delete') {
      e.preventDefault();
      const otp = getOtpValue();
      otp[activeInput] = '';
      handleOtpChange(otp);
      focusInput(activeInput);
    } else if (e.keyCode === LEFT_ARROW || e.key === 'ArrowLeft') {
      e.preventDefault();
      focusPrevInput();
    } else if (e.keyCode === RIGHT_ARROW || e.key === 'ArrowRight') {
      e.preventDefault();
      const firstEmptyIdx = getFirstEmptyIndex();
      // Allow moving right only if next box is within sequential bounds
      // (either has a value or is the first empty box)
      if (activeInput + 1 <= firstEmptyIdx) {
        focusNextInput();
      }
    } else if (
      e.keyCode === SPACEBAR ||
      e.key === ' ' ||
      e.key === 'Spacebar' ||
      e.key === 'Space'
    ) {
      e.preventDefault();
    }
  };

  // The content may not have changed, but some input took place hence change the focus
  const handleOnInput = (e) => {
    const { isInputNum, numInputs } = props;
    let inputValue = e.target.value;

    // Filter non-numeric characters for numeric inputs
    if (isInputNum) {
      const filteredValue = inputValue.replace(/\D/g, '');
      if (filteredValue !== inputValue) {
        // Non-numeric characters were entered, remove them
        e.target.value = filteredValue;
        inputValue = filteredValue;
        if (props.showToast) {
          props.showToast();
        }
      }
    }

    // Mobile keyboard autofill (one-time-code): multiple digits in first input
    if (inputValue.length > 1) {
      const digits = (isInputNum ? inputValue.replace(/\D/g, '') : inputValue)
        .slice(0, numInputs)
        .split('');
      const currentOtp = getOtpValue();
      const otp = Array(numInputs)
        .fill('')
        .map((_, idx) => digits[idx] ?? currentOtp[idx] ?? '');
      handleOtpChange(otp);
      focusInput(Math.min(digits.length, numInputs) - 1);
      return;
    }

    if (isInputValueValid(inputValue)) {
      focusNextInput();
    } else {
      // This is a workaround for dealing with keyCode "229 Unidentified" on Android.

      if (!isInputNum) {
        const { nativeEvent } = e;

        if (
          nativeEvent.data === null &&
          nativeEvent.inputType === 'deleteContentBackward'
        ) {
          e.preventDefault();
          changeCodeAtFocus('');
          focusPrevInput();
        }
      }
    }
  };

  const renderInputs = () => {
    const {
      numInputs,
      inputStyle,
      focusStyle,
      separator,
      isDisabled,
      disabledStyle,
      hasErrored,
      errorStyle,
      shouldAutoFocus,
      isInputNum,
      isInputSecure,
      className,
      onBlur,
      isFocus
    } = props;

    const inputs: React.ReactNode[] = [];
    const otp = getOtpValue();
    const placeholder = getPlaceholderValue();
    const dataCy = props['data-cy'];
    const dataTestId = props['data-testid'];

    // Calculate first empty index once for all inputs
    const firstEmptyIdx = getFirstEmptyIndex();

    for (let i = 0; i < numInputs; i++) {
      inputs.push(
        <SingleOtpInput
          placeholder={placeholder && placeholder[i]}
          key={i}
          index={i}
          maxLengthForFirst={numInputs}
          focus={activeInput === i}
          value={otp && otp[i]}
          onChange={handleOnChange}
          onKeyDown={handleOnKeyDown}
          onInput={handleOnInput}
          onPaste={handleOnPaste}
          onFocus={(e) => {
            // Enforce sequential filling: if trying to focus on a box beyond
            // the first empty box, redirect to the first empty box
            if (i > firstEmptyIdx) {
              // Prevent a brief focus style flash on the wrong input (mobile/desktop)
              // by blurring the tapped input before redirecting focus.
              if (e?.target && typeof e.target.blur === 'function') {
                e.target.blur();
              }
              setActiveInput(firstEmptyIdx);
              return;
            }
            setActiveInput(i);
            e.target.select();
          }}
          separator={separator}
          inputStyle={inputStyle}
          focusStyle={focusStyle}
          isLastChild={i === numInputs - 1}
          isDisabled={isDisabled}
          disabledStyle={disabledStyle}
          hasErrored={hasErrored}
          errorStyle={errorStyle}
          shouldAutoFocus={shouldAutoFocus}
          isInputNum={isInputNum}
          isInputSecure={isInputSecure}
          className={className}
          data-cy={dataCy && `${dataCy}-${i}`}
          data-testid={dataTestId && `${dataTestId}-${i}`}
          onBlur={onBlur}
          isFocus={isFocus}
        />
      );
    }

    return inputs;
  };

  const { containerStyle } = props;

  return (
    <div
      style={Object.assign(
        { display: 'flex' },
        isStyleObject(containerStyle) && containerStyle
      )}
      className={!isStyleObject(containerStyle) ? containerStyle : ''}
    >
      {renderInputs()}
    </div>
  );
};

export default OtpInput;
